import {Accordion, Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Container} from 'src/design/atoms/Container';
import {List} from 'src/design/atoms/List';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconAlertCircle, IconExternalLink} from '@tabler/icons-react';
import {useLocation} from 'react-router-dom';

interface EmbeddedBrowserPageProps {
  browserInfo?: string;
  originalUrl: string;
}

const EmbeddedBrowserPage = ({
  browserInfo,
  originalUrl,
}: EmbeddedBrowserPageProps) => {
  const location = useLocation();
  const stateBrowserInfo = location.state?.browserInfo;
  const displayBrowserInfo = browserInfo || stateBrowserInfo;

  return (
    <Container size={420} my={40}>
      <Stack>
        <Title ta="center">Open in Your Browser</Title>
        <Text c="dimmed" size="sm" ta="center">
          This application must be opened in your regular web browser to work.
        </Text>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack gap="lg">
            <Text c="dimmed" size="sm">
              You're currently viewing this application inside another app (like
              Teams, Gmail, Slack, or Outlook). This application cannot work
              properly within another app and must be opened in your regular web
              browser.
            </Text>

            <Button
              size="md"
              onClick={() => {
                window.open(originalUrl, '_system');
              }}
              fullWidth
              leftSection={<IconExternalLink size={16} />}>
              Open in Browser
            </Button>

            {displayBrowserInfo && (
              <Alert color="gray" icon={<IconAlertCircle />}>
                <Text c="dimmed" size="sm">
                  Detected: {displayBrowserInfo}
                </Text>
              </Alert>
            )}
          </Stack>
        </Paper>

        <Accordion variant="contained">
          <Accordion.Item value="instructions-apple">
            <Accordion.Control>
              <Text size="sm">iPhone/iPad</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Text c="dimmed" size="xs">
                  <strong>Option 1: Use the button above</strong>
                </Text>
                <Text c="dimmed" size="xs">
                  Tap the "Open in Browser" button. This should open the app in
                  Safari.
                </Text>

                <Text c="dimmed" size="xs" mt="sm">
                  <strong>Option 2: Manual steps</strong>
                </Text>
                <List c="dimmed" size="xs" spacing="xs">
                  <List.Item>
                    Tap and hold the link that brought you here
                  </List.Item>
                  <List.Item>Select "Open in Safari" from the menu</List.Item>
                  <List.Item>Or copy the link and paste it in Safari</List.Item>
                </List>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="instructions-android">
            <Accordion.Control>
              <Text size="sm">Android</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Text c="dimmed" size="xs">
                  <strong>Option 1: Use the button above</strong>
                </Text>
                <Text c="dimmed" size="xs">
                  Tap the "Open in Browser" button. This should open the app in
                  Chrome or your default browser.
                </Text>

                <Text c="dimmed" size="xs" mt="sm">
                  <strong>Option 2: Manual steps</strong>
                </Text>
                <List c="dimmed" size="xs" spacing="xs">
                  <List.Item>
                    Tap and hold the link that brought you here
                  </List.Item>
                  <List.Item>
                    Select "Open in browser" or "Open in Chrome"
                  </List.Item>
                  <List.Item>
                    Or copy the link and paste it in your browser
                  </List.Item>
                </List>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="instructions">
            <Accordion.Control>
              <Text size="sm">Desktop/Computer</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Text c="dimmed" size="xs">
                  <strong>Option 1: Use the button above</strong>
                </Text>
                <Text c="dimmed" size="xs">
                  Click the "Open in Browser" button to open this application in
                  your regular web browser.
                </Text>

                <Text c="dimmed" size="xs" mt="sm">
                  <strong>Option 2: Manual steps</strong>
                </Text>
                <List c="dimmed" size="xs" spacing="xs">
                  <List.Item>
                    Copy the web address from your address bar
                  </List.Item>
                  <List.Item>
                    Open your web browser (Chrome, Firefox, Safari, or Edge)
                  </List.Item>
                  <List.Item>Paste the address and press Enter</List.Item>
                </List>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="why">
            <Accordion.Control>
              <Text size="sm">Why is this needed?</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Text c="dimmed" size="xs">
                When you open this application inside another app (like Teams,
                Gmail, Slack, or Outlook), it cannot access the features it
                needs to work. The application will not function properly in
                this environment. You must open it in your regular web browser
                to use the application.
              </Text>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </Container>
  );
};

export default EmbeddedBrowserPage;
